import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../../hooks/useScollToTop.js";
import PartenaireKataCard from "./PartenaireKataCard";
import CreatePartenaireModal from "./CreatePartenaireModal";
import { departementsEtRegions } from "./donnees.js";
import { getAllPartenaires } from "../../../services/partenaireskataServices";
import {
  FaFilter,
  FaPlus,
  FaRedo,
  FaMapMarkedAlt,
  FaUsers,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import CarteFrance from "./CarteFrance";
import HeroImage from "../../../components/Hero/HeroImage";

const GRADES = [
  "1er Dan",
  "2ème Dan",
  "3ème Dan",
  "4ème Dan",
  "5ème Dan",
  "6ème Dan",
  "7ème Dan",
  "8ème Dan",
  "9ème Dan",
  "10ème Dan",
];

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const normalizeDeptCode = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return "";
  if (/^\d+$/.test(str)) {
    return String(parseInt(str, 10));
  }
  return str.toUpperCase();
};

function StatChip({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PartenairesKataPage() {
  useScrollToTop();

  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  const [region, setRegion] = useState("");
  const [departement, setDepartement] = useState("");
  const [passageGrade, setPassageGrade] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchPartenaires = async () => {
    try {
      setLoading(true);
      const allPartenaires = await getAllPartenaires();
      setPartenaires(Array.isArray(allPartenaires) ? allPartenaires : []);
    } catch (error) {
      console.error("Erreur chargement partenaires kata :", error);
      setPartenaires([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartenaires();
  }, []);

  const regions = useMemo(
    () => [...new Set(departementsEtRegions.map((item) => item.region))].sort(),
    []
  );

  const departementsForRegion = useMemo(() => {
    if (!region) return [];
    return departementsEtRegions.filter((item) => item.region === region);
  }, [region]);

  // Pour la carte : on filtre par région + grade, mais PAS par département
  const mapPartenaires = useMemo(() => {
    let results = [...partenaires];

    if (region) {
      results = results.filter((partenaire) => partenaire.region === region);
    }

    if (passageGrade) {
      results = results.filter(
        (partenaire) => partenaire.passage_grade === passageGrade
      );
    }

    return results;
  }, [partenaires, region, passageGrade]);

  // Pour la liste : on applique aussi le filtre département
  const filteredPartenaires = useMemo(() => {
    let results = [...mapPartenaires];

    if (departement) {
      const wantedDept = normalizeDeptCode(departement);
      results = results.filter(
        (partenaire) =>
          normalizeDeptCode(partenaire?.departement) === wantedDept
      );
    }

    return results.sort((a, b) => {
      const an = normalize(`${a.nom} ${a.prenom}`);
      const bn = normalize(`${b.nom} ${b.prenom}`);
      return an.localeCompare(bn);
    });
  }, [mapPartenaires, departement]);

  const stats = useMemo(() => {
    const total = partenaires.length;
    const visible = filteredPartenaires.length;
    const regionsCovered = new Set(
      partenaires.map((p) => p.region).filter(Boolean)
    ).size;

    return { total, visible, regionsCovered };
  }, [partenaires, filteredPartenaires]);

  const refreshList = async () => {
    await fetchPartenaires();
    setCreateModalOpen(false);
  };

  const resetFilters = () => {
    setRegion("");
    setDepartement("");
    setPassageGrade("");
  };

  return (
    <div className="w-full pt-[96px] font-satoshi">
      <HeroImage
        title="Recherche de partenaires Kata"
        subtitle="Déposez votre annonce et trouvez des partenaires de Kata pour vos entraînements et passages de grade."
        imageUrl="/img/cover/cover_kata.jpg"
      />

      <div className="mx-auto max-w-[1280px] px-4 pb-10 pt-6">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Kata
                </p>
                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Mise en relation nationale
                </h1>
                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <p>✅ Déposez votre annonce pour trouver un partenaire de Kata.</p>
                  <p>✅ Filtrez par région, département et grade.</p>
                  <p>
                    ✅ Seul le dépositaire de l’annonce peut la modifier ou la
                    supprimer avec son mot de passe.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[720px]">
                <StatChip
                  icon={<FaUsers />}
                  label="Annonces"
                  value={stats.total}
                  accent="indigo"
                />
                <StatChip
                  icon={<FaFilter />}
                  label="Résultats"
                  value={stats.visible}
                  accent="emerald"
                />
                <StatChip
                  icon={<FaMapMarkedAlt />}
                  label="Régions"
                  value={stats.regionsCovered}
                  accent="amber"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Filtres
              </p>
              <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                Affinez votre recherche
              </h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Cliquez sur un département de la carte ou utilisez les filtres
                ci-dessous.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaRedo />
                Réinitialiser
              </button>

              <Button
                text="Créer une annonce"
                icon={FaPlus}
                onClick={() => setCreateModalOpen(true)}
                className="!h-11 !rounded-2xl !px-4 gap-2"
              />
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <select
              onChange={(e) => {
                setRegion(e.target.value);
                setDepartement("");
              }}
              value={region}
              className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="">Toutes les régions</option>
              {regions.map((reg, index) => (
                <option key={index} value={reg}>
                  {reg}
                </option>
              ))}
            </select>

            <select
              onChange={(e) => setDepartement(normalizeDeptCode(e.target.value))}
              value={departement}
              className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              disabled={!region}
            >
              <option value="">Tous les départements</option>
              {departementsForRegion.map((dep, index) => (
                <option key={index} value={normalizeDeptCode(dep.code)}>
                  {dep.departement}
                </option>
              ))}
            </select>

            <select
              onChange={(e) => setPassageGrade(e.target.value)}
              value={passageGrade}
              className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="">Tous les grades</option>
              {GRADES.map((grade, index) => (
                <option key={index} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
              {filteredPartenaires.length} annonce
              {filteredPartenaires.length > 1 ? "s" : ""}
            </span>

            {region && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                Région : {region}
              </span>
            )}

            {departement && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                Département : {departement}
              </span>
            )}

            {passageGrade && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                Grade : {passageGrade}
              </span>
            )}
          </div>
        </div>

        <div className="mt-5 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Carte
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Répartition géographique des annonces
            </h2>
          </div>

          <CarteFrance
            partenaires={mapPartenaires}
            selectedDepartement={departement}
            onSelectDepartement={(code) => {
              const normalizedCode = normalizeDeptCode(code);

              const depData = departementsEtRegions.find(
                (d) => normalizeDeptCode(d.code) === normalizedCode
              );

              if (depData?.region) {
                setRegion(depData.region);
              }

              setDepartement(normalizedCode);
            }}
          />
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-14 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              Chargement des annonces…
            </div>
          ) : filteredPartenaires.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <FaUsers className="text-2xl" />
              </div>

              <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
                Aucun partenaire trouvé
              </h3>

              <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                Aucun résultat ne correspond à vos filtres actuels.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredPartenaires.map((partenaire) => (
                <PartenaireKataCard
                  key={partenaire.id}
                  partenaire={partenaire}
                  refreshPartenairesList={refreshList}
                />
              ))}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm italic text-slate-500 dark:text-slate-400">
          RGPD : les coordonnées publiées dans une annonce restent visibles tant
          que l’annonce n’est pas supprimée par son dépositaire.
        </p>
      </div>

      <CreatePartenaireModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        refreshList={refreshList}
      />
    </div>
  );
}